
import ReactECharts from 'echarts-for-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Thermometer, Zap, Fuel } from 'lucide-react';

export const EngineChart = () => {
  const option = {
    backgroundColor: 'transparent',
    title: {
      text: '发动机性能趋势',
      left: 'center',
      top: 10,
      textStyle: {
        fontSize: 16,
        fontWeight: 500,
        color: 'hsl(210, 20%, 95%)'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: 'hsl(205, 100%, 50%)'
        }
      },
      backgroundColor: 'hsl(220, 25%, 12%)',
      borderColor: 'hsl(205, 100%, 50%)',
      borderWidth: 1,
      textStyle: {
        color: 'hsl(210, 20%, 95%)'
      }
    },
    legend: {
      data: ['温度', '转速', '油耗'],
      bottom: 15,
      textStyle: {
        color: 'hsl(210, 20%, 95%)'
      },
      itemStyle: {
        borderWidth: 0
      }
    },
    grid: {
      left: '8%',
      right: '8%',
      bottom: '20%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['00:00', '02:00', '04:00', '06:00', '08:00', '10:00', '12:00'],
      axisLine: {
        lineStyle: {
          color: 'hsl(220, 25%, 20%)'
        }
      },
      axisLabel: {
        color: 'hsl(215, 15%, 70%)',
        fontSize: 12
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: 'hsl(220, 25%, 16%)',
          type: 'dashed'
        }
      }
    },
    yAxis: [
      {
        type: 'value',
        name: '温度(°C)',
        nameTextStyle: {
          color: 'hsl(215, 15%, 70%)'
        },
        position: 'left',
        axisLine: {
          lineStyle: {
            color: 'hsl(220, 25%, 20%)'
          }
        },
        axisLabel: {
          formatter: '{value}°C',
          color: 'hsl(215, 15%, 70%)'
        },
        splitLine: {
          lineStyle: {
            color: 'hsl(220, 25%, 16%)',
            type: 'dashed'
          }
        }
      },
      {
        type: 'value',
        name: '转速(RPM)',
        nameTextStyle: {
          color: 'hsl(215, 15%, 70%)'
        },
        position: 'right',
        axisLine: {
          lineStyle: {
            color: 'hsl(220, 25%, 20%)'
          }
        },
        axisLabel: {
          formatter: '{value} RPM',
          color: 'hsl(215, 15%, 70%)'
        },
        splitLine: {
          show: false
        }
      }
    ],
    series: [
      {
        name: '温度',
        type: 'line',
        yAxisIndex: 0,
        data: [80, 82, 85, 88, 92, 89, 85],
        smooth: true,
        lineStyle: {
          color: '#FF6B6B',
          width: 3,
          shadowColor: 'rgba(255, 107, 107, 0.3)',
          shadowBlur: 10
        },
        itemStyle: {
          color: '#FF6B6B',
          borderWidth: 2,
          borderColor: '#FFFFFF'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(255, 107, 107, 0.3)' },
              { offset: 1, color: 'rgba(255, 107, 107, 0.05)' }
            ]
          }
        }
      },
      {
        name: '转速',
        type: 'line',
        yAxisIndex: 1,
        data: [1800, 1820, 1850, 1780, 1750, 1790, 1800],
        smooth: true,
        lineStyle: {
          color: 'hsl(205, 100%, 50%)',
          width: 3,
          shadowColor: 'rgba(0, 153, 255, 0.3)',
          shadowBlur: 10
        },
        itemStyle: {
          color: 'hsl(205, 100%, 50%)',
          borderWidth: 2,
          borderColor: '#FFFFFF'
        }
      },
      {
        name: '油耗',
        type: 'bar',
        yAxisIndex: 0,
        data: [45, 47, 50, 48, 52, 49, 46],
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'hsl(120, 70%, 50%)' },
              { offset: 1, color: 'hsl(120, 70%, 30%)' }
            ]
          },
          borderRadius: [4, 4, 0, 0],
          shadowColor: 'rgba(34, 197, 94, 0.3)',
          shadowBlur: 10
        }
      }
    ]
  };

  const statusData = [
    { label: '温度状态', value: '89°C', icon: Thermometer, status: 'normal' },
    { label: '转速状态', value: '1800 RPM', icon: Zap, status: 'normal' },
    { label: '油耗状态', value: '46 L/h', icon: Fuel, status: 'normal' },
    { label: '负荷状态', value: '75%', icon: TrendingUp, status: 'normal' }
  ];

  return (
    <Card className="tech-card">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium text-foreground">发动机数据分析</CardTitle>
          <Badge className="status-active">实时监控</Badge>
        </div>
        
        {/* 状态指示器 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {statusData.map((item, index) => (
            <div key={index} className="flex items-center space-x-2 p-2 rounded-lg bg-muted/30">
              <item.icon className="h-4 w-4 text-primary" />
              <div>
                <div className="text-xs text-muted-foreground">{item.label}</div>
                <div className="text-sm font-medium text-foreground">{item.value}</div>
              </div>
            </div>
          ))}
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <ReactECharts
          option={option}
          style={{ height: '350px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </CardContent>
    </Card>
  );
};
